import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Select, Table, Tooltip } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import moment, { Moment } from 'moment';
import { Link } from 'react-router-dom';
import { api, endpoints } from '../../api';
import {
  ApplicationAppointed,
  ApplicationCreatedSvg,
  ApplicationDeclineSvg,
  ApplicationDraftSvg,
  ApplicationOnProgressSvg,
} from '../../icons';
import { routerPath } from '../../router/routerPath';
import {
  FormBlock,
  BlueLayout,
  CustomButton,
  CustomDatePicker,
  CustomSelect,
  CustomTableContainer,
  FlexEndCol,
  FormInput,
  FormItem,
  TableIconBlock,
  ExpandedInfoContainer,
  ContactsBlock,
} from '../../styles/styled';
import { sortPublishers, sortStatus } from '../../utils/sortObject';
import {
  ApplicationFormsResponseType,
  ApplicationFormsType,
  ApplicationFormType,
  PaginationType,
  PublishersType,
} from '../NewApplicationPage/types';
import { ApplicationPageContainer } from './styled';
import { ApplicationSearchType, StatusesType, StatusType } from './types';

const { Option } = Select;

export const ApplicationPage = () => {
  const [applicationsState, setApplicationsState] = useState<ApplicationFormsType>([]);
  const [publishersState, setPublishersState] = useState<PublishersType>([]);
  const [openedCell, setOpenedSell] = useState<number[]>([]);
  const [paginationState, setPaginationState] = useState<PaginationType>({
    pagination: 1,
    amount: 0,
  });

  const [applicationSearch, setApplicationSearch] = useState<ApplicationSearchType>({});
  const [statusesState, setStatuses] = useState<StatusesType>([]);
  const [searchState, setSearchState] = useState<'search' | 'neutral' | 'empty'>('empty');
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  const handleStartDateChange = (date: Moment | null) => {
    setStartDate(date);
    if (endDate && date && date.isAfter(endDate)) {
      setEndDate(null);
    }

    if (date) setApplicationSearch({ ...applicationSearch, created_at: date.toString() });
    else setApplicationSearch({ ...applicationSearch, created_at: undefined });
  };

  const handleEndDateChange = (date: Moment | null) => {
    setEndDate(date);

    if (startDate && date && date.isBefore(startDate)) {
      setStartDate(null);
    }

    if (date) setApplicationSearch({ ...applicationSearch, created_to: date.toString() });
    else setApplicationSearch({ ...applicationSearch, created_to: undefined });
  };

  const disabledEndDate: RangePickerProps['disabledDate'] = (current: moment.Moment) => {
    if (!startDate) {
      return current && current > moment().endOf('day');
    }

    return current && current < moment(startDate, 'YYYY-MM-DD');
  };

  const disabledStartDate: RangePickerProps['disabledDate'] = (current: moment.Moment) => {
    if (!endDate) {
      return current && current > moment().endOf('day');
    }

    return current && current > moment(endDate, 'YYYY-MM-DD');
  };

  const applicationTableColumns = [
    {
      title: '№ п/п',
      dataIndex: 'index',
      key: 'index',
      render: (x, y, index: number) => {
        return <p>{(paginationState.pagination - 1) * 10 + index + 1}</p>;
      },
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (value: string) => {
        return <p>{moment(value).format('DD.MM.YYYY')}</p>;
      },
    },
    {
      title: '№ Заявления',
      key: 'id',
      dataIndex: 'id',
    },
    {
      title: 'Дата создания заявления',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => <p>{moment(value).format('DD.MM.YYYY')}</p>,
    },
    {
      title: 'Рег. номер',
      dataIndex: 'registration_number',
      key: 'registration_number',
      render: (value: string | null, application: ApplicationFormType) => {
        return application?.status?.id === 6 ? (
          <Link to={routerPath.editApplication + application.id}>
            <p className="link">{value ? value : 'Без номера'}</p>{' '}
          </Link>
        ) : (
          <Link to={routerPath.showApplication + application.id}>
            <p className="link">{value ? value : 'Без номера'}</p>{' '}
          </Link>
        );
      },
    },
    {
      title: 'ФИО заявителя',
      dataIndex: 'fio',
      key: 'fio',
      render: (value: string) => <p>{value}</p>,
    },

    {
      title: 'Наименование издательства',
      dataIndex: 'publisher',
      key: 'publisher',
      render: (value: string, application: ApplicationFormType) => {
        return <p>{publishersState.filter((el) => el.id === application.publisher?.id)[0]?.title} </p>;
      },
    },
    {
      title: 'Контактная информация',
      dataIndex: 'phone',
      key: 'phone',
      render: (value: string, application: ApplicationFormType) => {
        return (
          <ContactsBlock>
            <div className="text">{application.publisher?.phone && application.publisher.phone.split(';')[0]}</div>
            <div className="text">{application.publisher?.email}</div>
          </ContactsBlock>
        );
      },
    },
    {
      title: 'Кол-во учебников',
      dataIndex: 'textbooks',
      key: 'textbooks',
      className: 'books-amount-column',
      render: (value: string, application: ApplicationFormType) => {
        return (
          <Link to={routerPath.textbook + application.id} style={{ textDecoration: 'underline' }}>
            {application.line_of_textbooks?.map((textbook) => textbook.textbooks).flat().length}
          </Link>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'expand',
      render: (statusValue: StatusType, application: ApplicationFormType) => {
        return (
          <TableIconBlock
            onClick={() => {
              setOpenedSell(
                openedCell.includes(application?.key || 0)
                  ? openedCell.filter((el) => el !== application?.key)
                  : [...openedCell, application?.key || 0],
              );
            }}
          >
            {application?.status?.code === 'appointed' ? (
              <Tooltip title="Назначена экспертиза" placement="left">
                <div>
                  <ApplicationAppointed />
                </div>
              </Tooltip>
            ) : application?.status?.code === 'in_progress' ? (
              <Tooltip title="В работе" placement="left">
                <div>
                  <ApplicationOnProgressSvg />
                </div>
              </Tooltip>
            ) : application?.status?.code === 'draft' ? (
              <Tooltip title="Черновик" placement="left">
                <div>
                  <ApplicationDraftSvg />
                </div>
              </Tooltip>
            ) : application?.status?.code === 'created' ? (
              <Tooltip title="Новое" placement="left">
                <div>
                  <ApplicationCreatedSvg />
                </div>
              </Tooltip>
            ) : application?.status?.code === 'decline' ? (
              <Tooltip title="Отменено" placement="left">
                <div>
                  <ApplicationDeclineSvg />
                </div>
              </Tooltip>
            ) : (
              <Tooltip title="Назначена экспертиза" placement="left">
                <ApplicationCreatedSvg />
              </Tooltip>
            )}
            <div className={openedCell.includes(application?.key || 0) ? 'column-open rotate' : 'column-open'}>
              {'>'}
            </div>
          </TableIconBlock>
        );
      },
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        const statusesResponse = await api.get<StatusesType>(endpoints.getStatuses());

        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));

        setPublishersState(() => publishersResponse.data.sort(sortPublishers).filter((el) => !el.is_archived));

        setStatuses(() => statusesResponse.data.sort(sortStatus));
      } catch (err) {}
    })();
  }, []);

  const loadData = () => {
    const applicationSearchChecker = Object.values(applicationSearch).filter((el) => el !== undefined).length > 0;

    if (searchState !== 'empty' && applicationSearchChecker) {
      (async () => {
        const params = {
          publisherId: applicationSearch?.publisher,
          status: applicationSearch?.status,
          fio: applicationSearch?.fio,
          year: applicationSearch?.year,
          application_at: applicationSearch?.created_at
            ? moment(applicationSearch.created_at).format('YYYY-MM-DD')
            : undefined,
          application_to: applicationSearch?.created_to
            ? moment(applicationSearch.created_to).format('YYYY-MM-DD')
            : undefined,
          perPage: 10,
          page: paginationState.pagination - 1,
        };

        if (searchState === 'search' && applicationSearchChecker) {
          const paginationResponse = await api.get<ApplicationFormsResponseType>(
            endpoints.getApplicationByParams({
              ...params,
              perPage: 10,
            }),
          );

          setPaginationState({
            ...paginationState,
            amount: paginationResponse.data.count,
          });

          setApplicationsState(
            paginationResponse.data.applications.map((el, index) => {
              return { ...el, key: index };
            }),
          );

          return;
        }
      })();
    } else if (!applicationSearchChecker) {
      (async () => {
        const response = await api.get<ApplicationFormsResponseType>(
          endpoints.getApplications(paginationState.pagination - 1, 10),
        );

        setPaginationState((prevState) => {
          return { ...prevState, amount: response.data.count };
        });

        setApplicationsState(() => {
          return response.data.applications.map((el, index) => {
            return { ...el, key: index };
          });
        });
      })();
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [paginationState.pagination, searchState]);

  const searchButtonHandler = () => {
    setSearchState('search');
    setPaginationState({ ...paginationState, pagination: 1 });
    loadData();
  };

  const emptyButtonHandler = () => {
    setSearchState('empty');
    setApplicationSearch({});
  };

  return (
    <ApplicationPageContainer>
      <BlueLayout>Заявления на проведение экспертизы</BlueLayout>
      <FormBlock>
        <Form>
          <Row gutter={[15, 15]}>
            <Col span={12}>
              <FormItem>
                <p>ФИО заявителя</p>
                <FormInput
                  value={applicationSearch.fio}
                  onChange={(e) =>
                    setApplicationSearch({
                      ...applicationSearch,
                      fio: e.target.value.length > 0 ? e.target.value : undefined,
                    })
                  }
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>Наименование издательства</p>
                <CustomSelect
                  onChange={(value, option) => {
                    setApplicationSearch({
                      ...applicationSearch,
                      publisher: Object.values(option)[0] === '0' ? undefined : parseInt(Object.values(option)[0]),
                    });
                  }}
                  value={
                    applicationSearch.publisher
                      ? publishersState.find((el) => el.id === applicationSearch.publisher)?.title
                      : 'Все'
                  }
                  defaultValue="Все"
                  showSearch
                >
                  <Option key="0" value={0}>
                    Все
                  </Option>
                  {publishersState.map((el) => (
                    <Option key={el.id} value={el.title}>
                      {el.title}
                    </Option>
                  ))}
                </CustomSelect>
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem>
                <p>Дата регистрации заявления</p>
                <CustomDatePicker
                  disabledDate={disabledStartDate}
                  onChange={handleStartDateChange}
                  value={
                    applicationSearch?.created_at
                      ? moment(applicationSearch?.created_at.split('.').reverse().join('.'))
                      : undefined
                  }
                  placeholder=""
                  format="DD.MM.YYYY"
                  allowClear
                />
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem>
                <p></p>
                <CustomDatePicker
                  disabledDate={disabledEndDate}
                  onChange={handleEndDateChange}
                  value={
                    applicationSearch?.created_to
                      ? moment(applicationSearch.created_to.split('.').reverse().join('.'))
                      : undefined
                  }
                  placeholder=""
                  format="DD.MM.YYYY"
                  allowClear
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem>
                <p>Статус</p>
                <CustomSelect
                  onChange={(value, option) => {
                    setApplicationSearch({
                      ...applicationSearch,
                      status: parseInt(Object.values(option)[0]) === 0 ? undefined : parseInt(Object.values(option)[0]),
                    });
                  }}
                  value={
                    applicationSearch.status
                      ? statusesState.find((el) => el.id === applicationSearch.status)?.name
                      : 'Все'
                  }
                  defaultValue="Все"
                  showSearch
                >
                  <Option key="0" value={0}>
                    Все
                  </Option>
                  {statusesState.map((el) => (
                    <Option key={el.id} value={el.name}>
                      {el.name}
                    </Option>
                  ))}
                </CustomSelect>
              </FormItem>
            </Col>
            <Col span={6}>
              <FormItem redstar>
                <p>Год поступления материала</p>
                <CustomSelect
                  value={applicationSearch.year === 0 ? '' : applicationSearch.year}
                  onChange={(value) =>
                    setApplicationSearch({
                      ...applicationSearch,
                      year: value as number,
                    })
                  }
                  showSearch
                  allowClear
                >
                  <Option value={2025} key={2025}>
                    2025
                  </Option>
                  <Option value={2024} key={2024}>
                    2024
                  </Option>
                  <Option value={2023} key={2023}>
                    2023
                  </Option>
                  <Option value={2022} key={2022}>
                    2022
                  </Option>
                  <Option value={2021} key={2021}>
                    2021
                  </Option>
                  <Option value={2020} key={2020}>
                    2020
                  </Option>
                </CustomSelect>
              </FormItem>
            </Col>
            <Col span={18}></Col>
            <FlexEndCol span={12}>
              <CustomButton onClick={searchButtonHandler}>Найти</CustomButton>
            </FlexEndCol>
            <FlexEndCol span={12}>
              <CustomButton lined onClick={emptyButtonHandler}>
                Очистить
              </CustomButton>
            </FlexEndCol>
          </Row>
        </Form>
      </FormBlock>

      <CustomTableContainer>
        <Link to={routerPath.newApplication}>
          <CustomButton style={{ float: 'right' }} width="300px" lined>
            зарегистрировать заявление
          </CustomButton>
        </Link>
        <Table
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedInfoContainer>
                <div className="columns">
                  <div className="column">Дата и время изменения</div>
                  <div className="column">Статус</div>
                  <div className="column">Контактная информация заявителя</div>
                </div>
                <div className="rows">
                  <div className="row">
                    {record.updated_at && record.updated_at.split('T')[0].split('-').reverse().join('.') + ' '}
                    {record.updated_at &&
                      record.updated_at.split('T')[1].split('+')[0].split(':').splice(0, 2).join(':')}
                  </div>
                  <div className="row">
                    {record?.status?.code === 'appointed' ? (
                      <ApplicationAppointed />
                    ) : record?.status?.code === 'in_progress' ? (
                      <ApplicationOnProgressSvg />
                    ) : (
                      <ApplicationCreatedSvg />
                    )}
                    {record?.status?.name}
                  </div>
                  <div className="row">
                    <div className="row-element">{record.publisher?.email} </div>
                    <div className="row-element">
                      {record.publisher?.phone && record.publisher?.phone.split(';').join(', ')}
                    </div>
                  </div>
                </div>
              </ExpandedInfoContainer>
            ),

            expandIconColumnIndex: -1,
            onExpand: () => true,
            expandRowByClick: false,
            defaultExpandedRowKeys: openedCell,
            expandedRowKeys: openedCell,
          }}
          bordered
          pagination={{
            position: ['bottomRight'],
            current: paginationState.pagination,
            onChange: (value) => setPaginationState({ ...paginationState, pagination: value }),
            total: paginationState.amount,
            pageSize: 10,
            showSizeChanger: false,
          }}
          columns={applicationTableColumns}
          dataSource={applicationsState}
          className={'table'}
        ></Table>
      </CustomTableContainer>
    </ApplicationPageContainer>
  );
};
